import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const FEATURES = {
  zh: [
    { icon: '🎯', title: 'AI 情境行銷', sub: 'Scenario Marketing', desc: '一鍵生成完整行銷情境：簡報、EDM、部落格、問卷，AI 自動產出專業級內容' },
    { icon: '🌐', title: '智慧行銷網站', sub: 'Marketing Sites', desc: '自動建站、SEO 優化、多語系支援，從產品頁到活動頁一站搞定' },
    { icon: '🏠', title: 'HOUSE AI 不動產', sub: 'Real Estate AI', desc: 'AI 智慧房產平台：虛擬導覽、即時問答、自動推薦、客戶管理' },
    { icon: '📧', title: 'Email 行銷自動化', sub: 'Email Campaigns', desc: '排程發送、開信追蹤、AI 生成信件內容，提升轉換率' },
    { icon: '🤖', title: 'AI 客服機器人', sub: 'Chatbot & LINE Bot', desc: '24/7 智慧客服，支援網站嵌入、LINE 官方帳號、多輪對話' },
    { icon: '📊', title: '行銷漏斗分析', sub: 'Funnel Analytics', desc: '視覺化轉換漏斗、客戶旅程追蹤、數據驅動行銷決策' },
    { icon: '📱', title: '到達頁面產生器', sub: 'Landing Pages', desc: 'AI 一鍵生成高轉換率 Landing Page，A/B 測試即時優化' },
    { icon: '🔍', title: 'AI SEO 優化', sub: 'AEO & Citation', desc: 'AI 搜尋引擎曝光度監控、品牌引用追蹤、自動化 SEO 建議' },
  ],
  en: [
    { icon: '🎯', title: 'AI Scenario Marketing', sub: 'Scenario Marketing', desc: 'One-click full marketing scenario: presentations, EDMs, blogs, surveys — AI generates professional content' },
    { icon: '🌐', title: 'Smart Marketing Sites', sub: 'Marketing Sites', desc: 'Auto-build, SEO optimization, multilingual support — product pages to event pages in one place' },
    { icon: '🏠', title: 'HOUSE AI Real Estate', sub: 'Real Estate AI', desc: 'AI-powered property platform: virtual tours, instant Q&A, auto-recommendations, client management' },
    { icon: '📧', title: 'Email Marketing Automation', sub: 'Email Campaigns', desc: 'Scheduled sends, open tracking, AI-generated email content — boost conversion rates' },
    { icon: '🤖', title: 'AI Chatbot & LINE Bot', sub: 'Chatbot & LINE Bot', desc: '24/7 intelligent customer service — website embed, LINE Official Account, multi-turn conversation' },
    { icon: '📊', title: 'Marketing Funnel Analytics', sub: 'Funnel Analytics', desc: 'Visual conversion funnels, customer journey tracking, data-driven marketing decisions' },
    { icon: '📱', title: 'Landing Page Generator', sub: 'Landing Pages', desc: 'AI one-click high-conversion Landing Pages with real-time A/B testing optimization' },
    { icon: '🔍', title: 'AI SEO Optimization', sub: 'AEO & Citation', desc: 'AI search engine visibility monitoring, brand citation tracking, automated SEO recommendations' },
  ],
};

const STATS = {
  zh: [
    { value: '50+', label: '企業客戶' },
    { value: '10,000+', label: '行銷內容產出' },
    { value: '98%', label: '客戶滿意度' },
    { value: '3x', label: '平均轉換提升' },
  ],
  en: [
    { value: '50+', label: 'Enterprise Clients' },
    { value: '10,000+', label: 'Marketing Assets Generated' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '3x', label: 'Average Conversion Lift' },
  ],
};

const CASES = {
  zh: [
    { name: 'HOUSE AI 不動產平台', desc: 'AI 驅動的智慧房產行銷：虛擬導覽、客戶自動匹配、24/7 AI 問答', tag: '不動產科技', gradient: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' },
    { name: '數發局意向書自動化', desc: '政府採購案經銷商意向書全流程自動化：批次發送、追蹤、統計', tag: '政府標案', gradient: 'linear-gradient(135deg, #0c3547 0%, #1565c0 100%)' },
    { name: '情境行銷自動建站', desc: '輸入產品資訊，AI 自動生成完整行銷網站：首頁、商品頁、活動頁', tag: 'AI 建站', gradient: 'linear-gradient(135deg, #2d1b69 0%, #6a1b9a 100%)' },
  ],
  en: [
    { name: 'HOUSE AI Real Estate Platform', desc: 'AI-driven property marketing: virtual tours, auto client matching, 24/7 AI Q&A', tag: 'PropTech', gradient: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)' },
    { name: 'Government Tender LOI Automation', desc: 'Full-cycle automation for dealer intent letters in government procurement: batch send, tracking, statistics', tag: 'GovTech', gradient: 'linear-gradient(135deg, #0c3547 0%, #1565c0 100%)' },
    { name: 'AI Marketing Site Builder', desc: 'Enter product info, AI auto-generates a complete marketing site: homepage, product pages, event pages', tag: 'AI Builder', gradient: 'linear-gradient(135deg, #2d1b69 0%, #6a1b9a 100%)' },
  ],
};

const STEPS = {
  zh: [
    { step: '01', title: '輸入情境', desc: '描述產品特色與目標客群，AI 自動分析最佳行銷策略', color: '#6366f1' },
    { step: '02', title: 'AI 自動生成', desc: '一鍵生成簡報、EDM、網站、問卷等完整行銷素材', color: '#8b5cf6' },
    { step: '03', title: '追蹤轉換', desc: '即時監控開信率、點擊率、詢價率，持續優化成效', color: '#a78bfa' },
  ],
  en: [
    { step: '01', title: 'Define Your Scenario', desc: 'Describe your product and target audience. AI analyzes the optimal marketing strategy.', color: '#6366f1' },
    { step: '02', title: 'AI Auto-Generates', desc: 'One click to generate presentations, EDMs, websites, surveys — complete marketing assets.', color: '#8b5cf6' },
    { step: '03', title: 'Track Conversions', desc: 'Real-time monitoring of open rates, click rates, inquiry rates — continuously optimize results.', color: '#a78bfa' },
  ],
};

export default function MarComLandingPage({ onGoLogin }) {
  const { i18n } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Use platform language; fall back to zh for any non-en
  const lang = i18n.language === 'en' ? 'en' : 'zh';

  const toggleLang = () => {
    const next = lang === 'zh' ? 'en' : 'zh-TW';
    i18n.changeLanguage(next);
  };

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, []);

  const navBg = scrollY > 60 ? 'rgba(10,10,30,0.95)' : 'transparent';
  const features = FEATURES[lang];
  const stats = STATS[lang];
  const cases = CASES[lang];
  const steps = STEPS[lang];

  const copy = {
    zh: {
      navFeatures: '功能', navCases: '案例', navLogin: '登入後台',
      heroSub: 'AI 驅動的品牌內容與監測服務',
      heroStart: '免費開始使用 →', heroMore: '瞭解更多',
      featuresLabel: '全方位行銷功能', featuresSub: '八大核心模組，覆蓋行銷全流程',
      casesLabel: '客戶成功案例',
      howLabel: '三步驟啟動 AI 行銷',
      ctaTitle: '準備好升級你的行銷了嗎？',
      ctaSub: '加入 Comboware marcom AI Studio，讓 AI 成為你最強的行銷夥伴',
      ctaBtn: '立即開始 →',
      footerDesc: 'AI 驅動的品牌內容與監測服務',
      footerProduct: '產品', footerCompany: '公司',
      footerP1: 'AI 情境行銷', footerP2: '智慧建站', footerP3: 'Email 自動化', footerP4: 'HOUSE AI',
      footerC1: '宇聯科技 Comboware', footerC2: '聯絡我們', footerC3: '隱私政策',
    },
    en: {
      navFeatures: 'Features', navCases: 'Cases', navLogin: 'Sign In',
      heroSub: 'AI-Powered Brand Content & Intelligence Platform',
      heroStart: 'Get Started Free →', heroMore: 'Learn More',
      featuresLabel: 'All-in-One Marketing Features', featuresSub: 'Eight core modules covering the full marketing lifecycle',
      casesLabel: 'Success Stories',
      howLabel: '3 Steps to Launch AI Marketing',
      ctaTitle: 'Ready to upgrade your marketing?',
      ctaSub: 'Join Comboware marcom AI Studio — let AI become your most powerful marketing partner',
      ctaBtn: 'Start Now →',
      footerDesc: 'AI-Powered Brand Content & Intelligence Platform',
      footerProduct: 'Product', footerCompany: 'Company',
      footerP1: 'AI Scenario Marketing', footerP2: 'Smart Site Builder', footerP3: 'Email Automation', footerP4: 'HOUSE AI',
      footerC1: 'Comboware Inc.', footerC2: 'Contact Us', footerC3: 'Privacy Policy',
    },
  }[lang];

  return (
    <div style={{ background: '#0a0a1e', color: '#fff', minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ═══ STICKY NAV ═══ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: navBg, backdropFilter: 'blur(20px)',
        borderBottom: scrollY > 60 ? '1px solid rgba(255,255,255,0.06)' : 'none',
        transition: 'all 0.3s ease',
      }}>
        <div style={{
          maxWidth: 1200, margin: '0 auto',
          padding: isMobile ? '14px 20px' : '16px 48px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/marcom-ai/marcom-ai-favicon.svg" alt="marcom AI" style={{ width: 40, height: 40 }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: 0.5 }}>marcom AI</div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: 2, textTransform: 'uppercase' }}>by Comboware</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            {!isMobile && (
              <>
                <a href="#features" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>{copy.navFeatures}</a>
                <a href="#cases" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: 14, fontWeight: 500 }}>{copy.navCases}</a>
              </>
            )}
            {/* Language toggle */}
            <button
              onClick={toggleLang}
              style={{
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'rgba(255,255,255,0.8)', borderRadius: 8,
                padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                letterSpacing: 0.5, transition: 'all 0.2s',
              }}
              onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.14)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.08)'; }}
            >
              {lang === 'zh' ? '🇬🇧 EN' : '🇹🇼 中文'}
            </button>
            <button onClick={onGoLogin} style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: '#fff', border: 'none', borderRadius: 10,
              padding: isMobile ? '8px 16px' : '10px 24px',
              fontSize: 14, fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(99,102,241,0.4)', transition: 'transform 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.target.style.transform = 'translateY(-2px)'; e.target.style.boxShadow = '0 8px 30px rgba(99,102,241,0.5)'; }}
            onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 20px rgba(99,102,241,0.4)'; }}
            >
              {copy.navLogin}
            </button>
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section style={{
        position: 'relative', overflow: 'hidden',
        padding: isMobile ? '140px 20px 80px' : '180px 48px 120px',
        textAlign: 'center',
        background: 'radial-gradient(ellipse at 50% 0%, rgba(99,102,241,0.15) 0%, transparent 60%), radial-gradient(ellipse at 80% 50%, rgba(139,92,246,0.1) 0%, transparent 50%)',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div style={{ position: 'relative', maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 20, padding: '6px 18px', marginBottom: 28, fontSize: 13, color: '#a5b4fc' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', animation: 'mcPulse 2s infinite' }} />
            AI-Powered Marketing Platform
          </div>
          <style>{`@keyframes mcPulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>
          <h1 style={{ fontSize: isMobile ? 38 : 64, fontWeight: 900, lineHeight: 1.1, letterSpacing: -1.5, marginBottom: 24, background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 50%, #c4b5fd 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Comboware marcom AI Studio
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 22, fontWeight: 400, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, maxWidth: 650, margin: '0 auto 40px' }}>
            {copy.heroSub}
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onGoLogin} style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '16px 40px', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 40px rgba(99,102,241,0.4)', transition: 'all 0.3s ease' }}
              onMouseEnter={e => { e.target.style.transform = 'scale(1.05)'; }}
              onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}>
              {copy.heroStart}
            </button>
            <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', borderRadius: 14, padding: '16px 32px', fontSize: 16, fontWeight: 600, cursor: 'pointer', transition: 'all 0.3s ease' }}
              onMouseEnter={e => { e.target.style.background = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.06)'; }}>
              {copy.heroMore}
            </button>
          </div>
        </div>
      </section>

      {/* ═══ STATS BAR ═══ */}
      <section style={{ borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '40px 20px', background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: isMobile ? 24 : 0, textAlign: 'center' }}>
          {stats.map((s, i) => (
            <div key={i} style={{ borderRight: (!isMobile && i < 3) ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
              <div style={{ fontSize: isMobile ? 32 : 42, fontWeight: 900, color: '#a5b4fc', letterSpacing: -1 }}>{s.value}</div>
              <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section id="features" style={{ padding: isMobile ? '80px 20px' : '120px 48px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>PLATFORM FEATURES</div>
          <h2 style={{ fontSize: isMobile ? 28 : 42, fontWeight: 900, letterSpacing: -1, marginBottom: 16 }}>{copy.featuresLabel}</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 500, margin: '0 auto' }}>{copy.featuresSub}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 20, padding: 28, transition: 'all 0.3s ease', cursor: 'default' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; e.currentTarget.style.borderColor = 'rgba(99,102,241,0.3)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ fontSize: 32, marginBottom: 16 }}>{f.icon}</div>
              <h3 style={{ fontSize: 17, fontWeight: 800, marginBottom: 2, color: '#fff' }}>{f.title}</h3>
              <div style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, letterSpacing: 0.5, marginBottom: 12, textTransform: 'uppercase' }}>{f.sub}</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CASES ═══ */}
      <section id="cases" style={{ padding: isMobile ? '80px 20px' : '120px 48px', background: 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>SUCCESS STORIES</div>
            <h2 style={{ fontSize: isMobile ? 28 : 42, fontWeight: 900, letterSpacing: -1, marginBottom: 16 }}>{copy.casesLabel}</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 24 }}>
            {cases.map((c, i) => (
              <div key={i} style={{ background: c.gradient, borderRadius: 24, padding: 32, position: 'relative', overflow: 'hidden', minHeight: 220, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', transition: 'transform 0.3s ease' }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.02)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                <div style={{ position: 'absolute', top: 0, right: 0, width: 200, height: 200, background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
                <span style={{ display: 'inline-block', background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '4px 12px', fontSize: 11, fontWeight: 700, marginBottom: 16, alignSelf: 'flex-start', letterSpacing: 0.5 }}>{c.tag}</span>
                <h3 style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>{c.name}</h3>
                <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, margin: 0 }}>{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section style={{ padding: isMobile ? '80px 20px' : '120px 48px', maxWidth: 1000, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#6366f1', letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>HOW IT WORKS</div>
        <h2 style={{ fontSize: isMobile ? 28 : 42, fontWeight: 900, letterSpacing: -1, marginBottom: 64 }}>{copy.howLabel}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 40 }}>
          {steps.map((s, i) => (
            <div key={i}>
              <div style={{ width: 64, height: 64, borderRadius: 20, background: `rgba(${s.color === '#6366f1' ? '99,102,241' : s.color === '#8b5cf6' ? '139,92,246' : '167,139,250'},0.15)`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24, fontWeight: 900, color: s.color }}>{s.step}</div>
              <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ padding: isMobile ? '80px 20px' : '100px 48px', textAlign: 'center', background: 'radial-gradient(ellipse at 50% 50%, rgba(99,102,241,0.12) 0%, transparent 60%)' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 900, letterSpacing: -1, marginBottom: 16 }}>{copy.ctaTitle}</h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', marginBottom: 40, lineHeight: 1.7 }}>{copy.ctaSub}</p>
          <button onClick={onGoLogin} style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '18px 48px', fontSize: 18, fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 40px rgba(99,102,241,0.4)', transition: 'all 0.3s ease' }}
            onMouseEnter={e => { e.target.style.transform = 'scale(1.05)'; }}
            onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}>
            {copy.ctaBtn}
          </button>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: isMobile ? '40px 20px' : '60px 48px 40px', background: 'rgba(0,0,0,0.3)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'center' : 'flex-start', gap: 32 }}>
          <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: isMobile ? 'center' : 'flex-start', marginBottom: 12 }}>
              <img src="/marcom-ai/marcom-ai-favicon.svg" alt="marcom AI" style={{ width: 36, height: 36 }} />
              <span style={{ fontSize: 15, fontWeight: 800 }}>marcom AI</span>
            </div>
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', maxWidth: 280, lineHeight: 1.6 }}>
              Comboware marcom AI Studio<br />{copy.footerDesc}
            </p>
          </div>
          <div style={{ display: 'flex', gap: isMobile ? 32 : 64, fontSize: 13 }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.8)' }}>{copy.footerProduct}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, color: 'rgba(255,255,255,0.4)' }}>
                <span>{copy.footerP1}</span><span>{copy.footerP2}</span><span>{copy.footerP3}</span><span>{copy.footerP4}</span>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 12, color: 'rgba(255,255,255,0.8)' }}>{copy.footerCompany}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, color: 'rgba(255,255,255,0.4)' }}>
                <span>{copy.footerC1}</span><span>{copy.footerC2}</span><span>{copy.footerC3}</span>
              </div>
            </div>
          </div>
        </div>
        <div style={{ maxWidth: 1200, margin: '32px auto 0', paddingTop: 24, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
          &copy; 2026 Comboware Inc. All rights reserved. — Comboware marcom AI Studio
        </div>
      </footer>
    </div>
  );
}
